import ReactDOM from 'react-dom';
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import PaymentMethodActions from '../../../utils/ajax/paymentMethodsAjax';
import {
  PaymentMethod as PaymentMethodModel,
} from '../../../structures/models/paymentMethod';
import PaymentMethodForm from './stripe/PaymentMethodForm';
import PaymentMethodsTable from './PaymentMethodsTable';

const emptyPaymentMethod = {
  name: '',
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  payment_method_id: '',
  country: 'US',
};

const PaymentMethodManagement = () => {
  const [firstLoad, setFirstLoad] = useState(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodModel[]>([]);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [deletePaymentProfile, setDeletePaymentProfile] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethodModel>(
    () => emptyPaymentMethod,
  );

  const initialize = async () => {
    setFirstLoad(false);
    setIsProcessing(true);
    const data = await PaymentMethodActions.index();
    setPaymentMethods(data);
    setIsProcessing(false);
  };

  useEffect(() => {
    if (firstLoad) {
      initialize();
    }
  }, []);

  // Show deletion prompt
  const showPaymentDeletion = (paymentMethod:PaymentMethodModel) => {
    setDeletePaymentProfile(paymentMethod.payment_method_id);
  };

  // Hide deletion prompt
  const hidePaymentDeletion = () => {
    setDeletePaymentProfile('');
  };

  const handleSubmit = useCallback(async (data) => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    try {
      if (currentPaymentMethod.payment_method_id) {
        await PaymentMethodActions.update(data, currentPaymentMethod.payment_method_id);
      } else {
        await PaymentMethodActions.create(data);
      }
      const updatedPaymentMethods = await PaymentMethodActions.index();
      setPaymentMethods(updatedPaymentMethods);
      setShowForm(false);
      setErrors([]);
    } catch (responseErrors: any) {
      setErrors(responseErrors);
    }

    setIsProcessing(false);
  }, [currentPaymentMethod, isProcessing]);

  const deletePaymentMethod = useCallback(async () => {
    if (isProcessing) {
      return;
    }
    setIsProcessing(true);

    try {
      await PaymentMethodActions.delete(deletePaymentProfile);
      setErrors([]);
    } catch (responseErrors: any) {
      setErrors(responseErrors);
      return;
    }

    const data = await PaymentMethodActions.index();
    setPaymentMethods(data);
    setDeletePaymentProfile('');
    setIsProcessing(false);
  }, [deletePaymentProfile, isProcessing]);

  const openCreateForm = () => {
    setCurrentPaymentMethod(emptyPaymentMethod);
    setShowForm(true);
  };

  const openEditForm = (paymentMethod: PaymentMethodModel) => {
    setCurrentPaymentMethod(paymentMethod);
    setShowForm(true);
  };

  return (
    <div>
      {showForm ? (
        <PaymentMethodForm
          setShowForm={setShowForm}
          currentPaymentMethod={currentPaymentMethod}
          handleSubmit={handleSubmit}
          isProcessing={isProcessing}
          setErrors={setErrors}
        />
      ) : (
        <PaymentMethodsTable
          paymentMethods={paymentMethods}
          openCreateForm={openCreateForm}
          openEditForm={openEditForm}
          isProcessing={isProcessing}
          showPaymentDeletion={showPaymentDeletion}
        />
      )}
      {errors.length > 0 && (
        <Alert variant="warning">
          <Alert.Heading>Error(s)</Alert.Heading>
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
      <Modal
        show={!!deletePaymentProfile}
        onHide={hidePaymentDeletion}
      >
        <Modal.Header>
          <Modal.Title>Delete Card?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to remove this payment method?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={deletePaymentMethod}
          >
            Yes, Delete this card
          </Button>
          <Button
            variant="secondary"
            onClick={hidePaymentDeletion}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaymentMethodManagement;

document.querySelectorAll('.react-payment-method-management').forEach((element) => {
  ReactDOM.render(<PaymentMethodManagement />, element);
});
